// Products API
import { apiRequest } from './request.js';
import { withCache } from './config.js';

export const productsAPI = {
    // Get all products (public)
    getAll: async (filters = {}) => {
        const queryParams = new URLSearchParams(filters).toString();
        const endpoint = queryParams ? `/products/?${queryParams}` : '/products/';

        return apiRequest(endpoint, {
            requireAuth: false
        });
    },

    // Get single product (public)
    getById: async (productId) => {
        return apiRequest(`/products/${productId}/`, {
            requireAuth: false
        });
    },

    // Create product (authenticated)
    create: async (productData) => {
        return apiRequest('/products/', {
            method: 'POST',
            body: JSON.stringify(productData)
        });
    },

    // Update product (authenticated)
    update: async (productId, updateData) => {
        return apiRequest(`/products/${productId}/`, {
            method: 'PATCH',
            body: JSON.stringify(updateData)
        });
    },

    // Delete product (authenticated)
    delete: async (productId) => {
        return apiRequest(`/products/${productId}/`, {
            method: 'DELETE'
        });
    },

    // Get product families (public)
    getFamilies: async () => {
        return withCache('product_families', () => apiRequest('/products/families/', {
            requireAuth: false
        }));
    },

    // Get product brands (public) - with caching
    getBrands: async () => {
        return withCache('product_brands', () => apiRequest('/products/brands/', {
            requireAuth: false
        }));
    },

    // Get product categories (public)
    getCategories: async () => {
        return withCache('product_categories', () => apiRequest('/products/categories/', {
            requireAuth: false
        }));
    },

    // Bulk upload products
    bulkUpload: async (businessId, products, options = {}) => {
        return apiRequest('/products/bulk-upload/', {
            method: 'POST',
            body: JSON.stringify({
                business_id: businessId,
                products,
                update_existing: options.updateExisting || false,
                skip_errors: options.skipErrors || true
            })
        });
    },

    // Save draft for bulk upload
    saveDraft: async (businessId, productsData, metadata = {}) => {
        return apiRequest('/products/drafts/', {
            method: 'POST',
            body: JSON.stringify({
                business_id: businessId,
                products_data: productsData,
                metadata
            })
        });
    },

    // List drafts
    getDrafts: async () => {
        return apiRequest('/products/drafts/');
    },

    // Get single draft
    getDraft: async (draftId) => {
        return apiRequest(`/products/drafts/${draftId}/`);
    },

    // Process (confirm) a draft
    processDraft: async (draftId, options = {}) => {
        return apiRequest(`/products/drafts/${draftId}/process/`, {
            method: 'POST',
            body: JSON.stringify({
                update_existing: options.updateExisting || false,
                skip_errors: options.skipErrors || true
            })
        });
    },

    // Delete draft
    deleteDraft: async (draftId) => {
        return apiRequest(`/products/drafts/${draftId}/`, {
            method: 'DELETE'
        });
    }
};

// Services API
export const servicesAPI = {
    // Get all services (public)
    getAll: async (filters = {}) => {
        const queryParams = new URLSearchParams(filters).toString();
        const endpoint = queryParams ? `/services/?${queryParams}` : '/services/';

        return apiRequest(endpoint, {
            requireAuth: false
        });
    },

    // Get single service (public)
    getById: async (serviceId) => {
        return apiRequest(`/services/${serviceId}/`, {
            requireAuth: false
        });
    },

    // Get service categories (public)
    getCategories: async () => {
        return withCache('service_categories', () => apiRequest('/services/categories/', {
            requireAuth: false
        }));
    }
};

// Categories API
export const categoriesAPI = {
    // Get all categories (public). Cacheado con withCache — el catálogo global
    // (page_size=5000&flat=true, ~1.24MB) lo pide StaticDataContext al arranque
    // + varios componentes de /advanced-search-results como fallback. Antes,
    // cada nav en la app disparaba un GET completo (medido: 23 hits ≈ 28MB
    // en una sesión). TTL largo porque las categorías rara vez cambian; una
    // publicación admin invalida el cache haciendo location.reload().
    getAll: async (params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = queryParams ? `/categories/?${queryParams}` : '/categories/';
        // La key incluye los params: distintos filtros se cachean por separado.
        return withCache(`categories_all_${queryParams || 'root'}`, () => apiRequest(endpoint, {
            requireAuth: false
        }), { ttl: 30 * 60 * 1000 });
    },

    // Get single category (public)
    getById: async (categoryId) => {
        return apiRequest(`/categories/${categoryId}/`, {
            requireAuth: false
        });
    },

    // Get primary categories (categories without parent)
    getPrimaries: async () => {
        return apiRequest('/categories/primaries/', {
            requireAuth: false
        });
    },

    // Search categorías con expansion de sinónimos (UX9). Devuelve [{id,name,slug,breadcrumb,via_synonym,level}]
    // maxLevel: corta profundidad (wizard de empresa usa 1; alta de producto usa todo el arbol).
    search: async (q, limit = 15, maxLevel = null) => {
        const params = new URLSearchParams({ q: q || '', limit: String(limit) });
        if (maxLevel !== null && maxLevel !== undefined) params.set('max_level', String(maxLevel));
        return apiRequest(`/categories/search/?${params.toString()}`, { requireAuth: false });
    },

    // Get categories with associated businesses
    getBusinessCategories: async () => {
        return withCache('business_categories', () => apiRequest('/categories/business-categories/', {
            requireAuth: false
        }));
    },

    // Get IDs de categorías que tienen empresas (directa o indirectamente
    // vía subcategorías). Usado para desactivar visualmente las
    // categorías vacías en el listado/panel de filtros y evitar
    // mostrar opciones sin contenido durante el lanzamiento.
    getActiveIds: async () => {
        return withCache('category_active_ids', () => apiRequest('/categories/active-ids/', {
            requireAuth: false
        }));
    },

    // Create category (admin only)
    create: async (categoryData) => {
        return apiRequest('/categories/', {
            method: 'POST',
            body: JSON.stringify(categoryData)
        });
    },

    // Update category (admin only)
    update: async (categoryId, categoryData) => {
        return apiRequest(`/categories/${categoryId}/`, {
            method: 'PATCH',
            body: JSON.stringify(categoryData)
        });
    },

    // Delete category (admin only)
    delete: async (categoryId) => {
        return apiRequest(`/categories/${categoryId}/`, {
            method: 'DELETE'
        });
    }
};
